import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, LogOut, Edit3, Database } from 'lucide-react';
import { MenuDropdown } from './MenuDropdown';
import { GlassPanel } from './GlassPanel';
import { EditProfileModal } from './EditProfileModal';
import { NotificationsBell } from './NotificationsBell';
import type { ProjectsTab } from '../views/ProjectsView';
import type { AdminTab } from '../views/AdminView';
import type { ContactsTab } from '../views/PeopleView';
import type { ContentItemType } from '../lib/db/types';
import { useAuth } from '../lib/auth/AuthProvider';
import { getShellScrollTop } from '../lib/shellLayout';
import { NAV_TABS } from '../lib/navStructure';
import { useFirestore } from '../lib/firebase';

/**
 * True when a *production* build is running without a real Firebase backend —
 * i.e. the `VITE_FIREBASE_*` config is missing in the host env, so the app has
 * silently fallen back to per-browser localStorage and data is NOT shared
 * between users. Dev (mock mode) is intentional and never flags. See AUDIT.md §4A.
 */
const isLocalDataFallback = import.meta.env.PROD && !useFirestore;

export type ViewKey =
  | 'dashboard'
  | 'files'
  | 'contracts'
  | 'tools'
  | 'chat'
  | 'content-engine'
  | 'email-marketing'
  | 'contacts'
  | 'customer-success'
  | 'projects'
  | 'tickets'
  | 'goals'
  | 'hyper-coder'
  | 'scoring-engine'
  | 'reports'
  | 'admin';

interface TopMenuProps {
  active: ViewKey;
  onSelect: (key: ViewKey) => void;
  /** Optional: when the Contacts dropdown picks an item, App lifts it
   *  to ContactsView's initial tab. */
  onSelectContactsTab?: (tab: ContactsTab) => void;
  /** Optional: when the Projects dropdown picks a sub-tab, App lifts
   *  it to ProjectsView's initial tab. */
  onSelectProjectsTab?: (tab: ProjectsTab) => void;
  /** Optional: when the Admin dropdown picks a sub-tab. */
  onSelectAdminTab?: (tab: AdminTab) => void;
  /** Optional: when a Content dropdown item deep-links a per-type
   *  workspace (e.g. Blog/SEO → the blog workspace). Called with null when
   *  a content-engine route carries no type, so plain "Content" clears any
   *  prior deep-link back to the type picker. */
  onSelectContentType?: (type: ContentItemType | null) => void;
}

// The 10-tab IA (tabs, dropdown groupings, and every route) lives in
// lib/navStructure.ts as pure data so the contract test can assert no
// nav entry dangles. This component only renders it.

export function TopMenu({
  active,
  onSelect,
  onSelectContactsTab,
  onSelectProjectsTab,
  onSelectAdminTab,
  onSelectContentType,
}: TopMenuProps) {
  const { user, hasRole, signOut } = useAuth();
  // open: which dropdown is currently open. Tracks both hover + click states.
  const [open, setOpen] = useState<ViewKey | null>(null);
  // pinned: when true, the dropdown stays open until clicked outside.
  // Toggled by clicking the parent button. Hover changes open transiently.
  const [pinned, setPinned] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const closeTimeout = useRef<number | undefined>();
  const shellRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  // Nav horizontal-scroll affordance: when the pill list is wider than its
  // track (high zoom / narrow window) show a slight fade + chevron at
  // whichever edge still has hidden items.
  const navListRef = useRef<HTMLUListElement>(null);
  const [navFade, setNavFade] = useState({ left: false, right: false });

  // Body scroll-shadow toggle. The document no longer scrolls — the
  // dedicated .app-shell__scroll container does (see shellLayout.ts) —
  // so read the container's scrollTop and listen in the CAPTURE phase:
  // element scroll events don't bubble, but they do pass a capturing
  // window listener, so this fires for the shell container too.
  useEffect(() => {
    const onScroll = () => {
      if (getShellScrollTop() > 4) document.body.classList.add('is-scrolled');
      else document.body.classList.remove('is-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  // Detect nav overflow to toggle the edge scroll-hints. Recomputes on
  // scroll, window resize (covers browser zoom), and any nav size change.
  useEffect(() => {
    const el = navListRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setNavFade({
        left: scrollLeft > 2,
        right: scrollLeft + clientWidth < scrollWidth - 2,
      });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, []);

  // Click-outside dismiss
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Dismiss if click is outside the shell AND outside any active portal dropdown
      if (
        !shellRef.current?.contains(target) &&
        !target.closest('[data-menu-portal="true"]')
      ) {
        setOpen(null);
        setPinned(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onEnter = (id: ViewKey, hasDropdown?: boolean) => {
    if (!hasDropdown) return;
    window.clearTimeout(closeTimeout.current);
    setOpen(id);
  };

  const onLeave = () => {
    if (pinned) return;
    window.clearTimeout(closeTimeout.current);
    closeTimeout.current = window.setTimeout(() => setOpen(null), 180);
  };

  const onClick = (id: ViewKey, hasDropdown?: boolean) => {
    // A top-level navigation to the Content Engine always means the type
    // picker — clear any prior Blog/SEO deep-link so the user isn't stuck
    // in the last workspace.
    if (id === 'content-engine') onSelectContentType?.(null);
    if (!hasDropdown) {
      onSelect(id);
      setOpen(null);
      setPinned(false);
      return;
    }
    // Dropdown buttons navigate to their parent view on click (so e.g.
    // clicking "Contacts" lands you in the CRM) AND pin the dropdown so
    // the sub-tabs (Accounts / Contacts / Prospects) stay available to
    // refine the landing. A second click on the same open button closes
    // the dropdown but keeps you on the view.
    onSelect(id);
    if (open === id && pinned) {
      setOpen(null);
      setPinned(false);
    } else {
      setOpen(id);
      setPinned(true);
    }
  };

  return (
    <GlassPanel
      variant="menu"
      animate={false}
      className="topmenu-shell"
      ref={shellRef}
      role="banner"
    >
      <div className="topmenu-brand">
        <img
          src="/logo-argoiq.png"
          alt=""
          aria-hidden="true"
          className="brand-logo"
        />
        <span>Argo&nbsp;OS</span>
      </div>

      <div className="topmenu-nav">
      <ul className="topmenu-list" role="menubar" ref={navListRef}>
        {NAV_TABS
          .filter((b) => !b.requiresRole || hasRole(b.requiresRole))
          .map((b) => {
          const Icon = b.icon;
          const isOpen = open === b.id;
          // Role-filter dropdown entries too (e.g. Content > Brand Brain
          // routes into AdminView, which non-admins cannot open).
          const items =
            b.items?.filter((i) => !i.requiresRole || hasRole(i.requiresRole)) ??
            null;
          const hasDropdown = !!items && items.length > 0;
          return (
            <li
              key={b.id}
              role="none"
              style={{ position: 'relative' }}
              onMouseEnter={() => onEnter(b.id, hasDropdown)}
              onMouseLeave={onLeave}
            >
              <button
                ref={(el) => {
                  if (el) btnRefs.current.set(b.id, el);
                  else btnRefs.current.delete(b.id);
                }}
                type="button"
                className="topmenu-btn"
                role="menuitem"
                data-active={active === b.id}
                data-open={isOpen}
                aria-haspopup={hasDropdown ? 'menu' : undefined}
                aria-expanded={isOpen ? true : undefined}
                aria-label={b.label}
                title={b.label}
                onClick={() => onClick(b.id, hasDropdown)}
              >
                <Icon size={14} strokeWidth={2.25} />
                <span className="topmenu-btn__label">{b.label}</span>
                {hasDropdown && (
                  <ChevronDown
                    className="topmenu-btn__caret"
                    size={12}
                    strokeWidth={2}
                  />
                )}
              </button>

              {isOpen && hasDropdown && items && (
                <MenuDropdown
                  items={items.map((item) => ({
                    id: item.id,
                    title: item.title,
                    sub: item.sub,
                    meta: item.meta,
                    icon: item.icon,
                    onSelect: () => {
                      // Every dropdown entry carries an explicit route
                      // (navStructure.ts, contract-tested) — resolve the
                      // optional sub-tab deep-link, then navigate. No
                      // per-tab special cases, no dead clicks.
                      const r = item.route;
                      if (r.contactsTab) onSelectContactsTab?.(r.contactsTab);
                      if (r.projectsTab) onSelectProjectsTab?.(r.projectsTab);
                      if (r.adminTab) onSelectAdminTab?.(r.adminTab);
                      if (r.view === 'content-engine') onSelectContentType?.(r.contentType ?? null);
                      onSelect(r.view);
                    },
                  }))}
                  anchorEl={btnRefs.current.get(b.id)}
                  onClose={() => {
                    setOpen(null);
                    setPinned(false);
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
        {navFade.left && (
          <span
            className="topmenu-scroll-hint topmenu-scroll-hint--left"
            aria-hidden="true"
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
          </span>
        )}
        {navFade.right && (
          <span
            className="topmenu-scroll-hint topmenu-scroll-hint--right"
            aria-hidden="true"
          >
            <ChevronRight size={16} strokeWidth={2.5} />
          </span>
        )}
      </div>

      <div className="topmenu-status">
        <NotificationsBell onNavigate={onSelect} onSelectContactsTab={onSelectContactsTab} />
        {isLocalDataFallback && (
          <span
            className="topmenu-localdata-pill"
            title={
              'No Firebase backend is configured for this deployment, so data is ' +
              'stored only in THIS browser and is not shared between users. ' +
              'Set VITE_FIREBASE_* (with VITE_USE_FIRESTORE=true) in the host env ' +
              'to use the shared database.'
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              color: '#fbbf24',
              background: 'rgba(251, 191, 36, 0.12)',
              border: '1px solid rgba(251, 191, 36, 0.35)',
              whiteSpace: 'nowrap',
            }}
          >
            <Database size={12} strokeWidth={2.25} aria-hidden="true" />
            Local data only
          </span>
        )}
        {user && (
          <div className="topmenu-user" title={user.email}>
            <button
              type="button"
              className="topmenu-user__avatar"
              aria-label="Edit profile"
              title="Edit profile"
              data-role={user.role}
              onClick={() => setProfileOpen(true)}
              style={{
                padding: 0,
                cursor: 'pointer',
                backgroundImage: user.photoURL ? `url("${user.photoURL}")` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                color: user.photoURL ? 'transparent' : undefined,
              }}
            >
              {(user.displayName || user.email).slice(0, 1).toUpperCase()}
            </button>
            <div className="topmenu-user__meta">
              <span className="topmenu-user__name">{user.displayName}</span>
              <span className="topmenu-user__role">{user.role}</span>
            </div>
            <button
              type="button"
              className="topmenu-user__signout"
              onClick={() => setProfileOpen(true)}
              aria-label="Edit profile"
              title="Edit profile"
            >
              <Edit3 size={13} strokeWidth={2} />
            </button>
            <button
              type="button"
              className="topmenu-user__signout"
              onClick={() => signOut()}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut size={13} strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
      {profileOpen && (
        <EditProfileModal onClose={() => setProfileOpen(false)} />
      )}
    </GlassPanel>
  );
}
